import React, { useState } from 'react';
import styled from 'styled-components';
import { Palette, MediaQuery } from 'core/config';
import { buildMeetingBookedThankYouUrl, withUtmFields } from 'core/utilities';
import { submitLeadCapture } from 'core/utilities/submitLeadCapture';

const HELP_OPTIONS = [
    'AI integration',
    'Software build',
    'Product design',
    'Strategy / audit',
    'Not sure yet',
];

export const StrategyCallForm: React.FunctionComponent = () =>
{
    const [ submitting, setSubmitting ] = useState(false);
    const [ error, setError ] = useState('');
    const [ form, setForm ] = useState({
        fullName: '',
        workEmail: '',
        phone: '',
        company: '',
        concern: '',
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) =>
    {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try
        {
            await submitLeadCapture(withUtmFields({
                fullName: form.fullName,
                workEmail: form.workEmail,
                phone: form.phone,
                company: form.company,
                concern: form.concern,
                type: 'strategy-call',
                source: window.location.pathname,
            }));

            window.location.href = buildMeetingBookedThankYouUrl();
        }
        catch
        {
            setError('Something went wrong. Please try again.');
        }
        finally
        {
            setSubmitting(false);
        }
    };

    return (
        <FormCard>
            <FormTitle>Book a Strategy Call</FormTitle>
            <FormSubtitle>
                Tell us what you're building. We'll review your goals and recommend the best next step.
            </FormSubtitle>
            <Form onSubmit={handleSubmit}>
                <FieldGroup>
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                        id="fullName"
                        name="fullName"
                        type="text"
                        required
                        placeholder="John Doe"
                        value={form.fullName}
                        onChange={handleChange}
                    />
                </FieldGroup>
                <FieldGroup>
                    <Label htmlFor="workEmail">Work Email</Label>
                    <Input
                        id="workEmail"
                        name="workEmail"
                        type="email"
                        required
                        placeholder="john@company.com"
                        value={form.workEmail}
                        onChange={handleChange}
                    />
                </FieldGroup>
                <FieldGroup>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        placeholder="+1 (555) 555-5555"
                        autoComplete="tel"
                        value={form.phone}
                        onChange={handleChange}
                    />
                </FieldGroup>
                <FieldGroup>
                    <Label htmlFor="company">Company</Label>
                    <Input
                        id="company"
                        name="company"
                        type="text"
                        required
                        placeholder="Company name"
                        value={form.company}
                        onChange={handleChange}
                    />
                </FieldGroup>
                <FieldGroup>
                    <Label htmlFor="concern">What can we help with?</Label>
                    <Select
                        id="concern"
                        name="concern"
                        required
                        value={form.concern}
                        onChange={handleChange}
                    >
                        <option value="">Select an option...</option>
                        {HELP_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </Select>
                </FieldGroup>
                {error && <ErrorText>{error}</ErrorText>}
                <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Book My Call'}
                </SubmitButton>
            </Form>
        </FormCard>
    );
};

const FormCard = styled.div`
    width: 100%;
    background: rgba(255, 255, 255, 0.94);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 16px;
    padding: 32px 24px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);

    @media ${MediaQuery.tablet} {
        padding: 36px 32px;
    }
`;

const FormTitle = styled.h2`
    font-size: 22px;
    line-height: 1.25;
    color: ${Palette.BLACK};
    margin: 0 0 10px;

    @media ${MediaQuery.tablet} {
        font-size: 26px;
    }
`;

const FormSubtitle = styled.p`
    font-family: 'Poppins-Light', sans-serif;
    font-size: 14px;
    line-height: 1.7;
    color: ${Palette.GREY};
    margin: 0 0 28px;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Label = styled.label`
    font-family: 'Poppins-Medium', sans-serif;
    font-size: 13px;
    letter-spacing: 0.04em;
    color: ${Palette.BLACK};
`;

const inputStyles = `
    width: 100%;
    padding: 13px 16px;
    border: 1px solid #D8D8D8;
    border-radius: 8px;
    font-family: 'Poppins-Light', sans-serif;
    font-size: 15px;
    color: #000;
    background: #fff;
    outline: none;
    transition: border-color 0.2s ease;
    box-sizing: border-box;

    &:focus {
        border-color: #000;
    }

    &::placeholder {
        color: #ADADAD;
    }
`;

const Input = styled.input`${inputStyles}`;

const Select = styled.select`
    ${inputStyles}
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23000' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    padding-right: 40px;
`;

const ErrorText = styled.p`
    color: ${Palette.RED};
    font-family: 'Poppins-Light', sans-serif;
    font-size: 14px;
    margin: 0;
`;

const SubmitButton = styled.button`
    margin-top: 4px;
    padding: 15px 32px;
    border: 1px solid ${Palette.BLACK};
    background-color: ${Palette.BLACK};
    color: ${Palette.WHITE};
    font-family: 'Poppins-Regular', sans-serif;
    font-size: 15px;
    border-radius: 50px;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover:not(:disabled) {
        background-color: ${Palette.WHITE};
        color: ${Palette.BLACK};
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    @media ${MediaQuery.tablet} {
        font-size: 16px;
    }
`;

